import OtpInput from '@/domains/shared/components/otpInputField/otpInputField';
import CsMask from '@/domains/shared/components/CsMask';
import { usePlatformInfo } from '@/domains/shared/hooks/useIsNative';
import Image from 'next/image';
import {
  HeadingRegular,
  BodyR,
  DetailB,
  CalloutB,
  Checkbox,
  Notification,
} from '@pensionbee/honeypot-ui';
import { Accordion } from '@pensionbee/honeypot-ui';
import { Button } from '@pensionbee/honeypot-ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { twoFaFaqItems } from '../twoFaFaqs';
import GreenBulletCheck from '@/images/green-bullet-checkmark.svg';
import { type AuthErrorCode } from '@/domains/auth/api/responseHelpers/error';

const errorMessages: Partial<Record<AuthErrorCode, string>> = {
  '2FA/INVALID-OTP':
    "Sorry, we don't recognise that code. Please try entering it again or ask for a new code.",
  '2FA/EXPIRED-OTP': 'Your code has expired. Please request a new code.',
};

type Props = {
  handleOTPSubmission: (code: string, trustDevice: boolean) => void;
  handleOTPResend: () => void;
  phoneNumber?: string;
  errorCode?: AuthErrorCode;
  otpError?: boolean;
  codeResent?: boolean;
  showTrustDevice?: boolean;
};

const ConfirmItsYou = ({
  handleOTPSubmission,
  handleOTPResend,
  phoneNumber,
  errorCode,
  otpError,
  codeResent,
  showTrustDevice: showTrustDeviceProp = false,
}: Props) => {
  const { isWeb } = usePlatformInfo();
  // Only offered on web, not in the native app.
  const showTrustDevice = showTrustDeviceProp && isWeb;
  const [otpValue, setOtpValue] = useState('');
  const { control, watch } = useForm<{ trustDevice: boolean }>({
    defaultValues: { trustDevice: false },
  });
  const trustDevice = watch('trustDevice');
  const [otpInputError, setOtpInputError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  useEffect(() => {
    if (errorCode) {
      setErrorMessage(errorMessages[errorCode]);
    }
  }, [errorCode]);

  useEffect(() => {
    if (otpError) {
      setOtpValue('');
      setOtpInputError(true);
    }
  }, [otpError]);

  const handleOtpChange = (value: string) => {
    setOtpValue(value);
    setOtpInputError(false);
  };

  const handleResendClick = () => {
    setOtpInputError(false);
    setOtpValue('');
    setErrorMessage(undefined);
    handleOTPResend();
  };

  const isOtpComplete = otpValue.length === 6;

  return (
    <>
      <div className="pb-28">
        <HeadingRegular as="h1">Confirm it’s you</HeadingRegular>
      </div>
      <div className="mb-[1.75rem]">
        <BodyR>
          To continue please enter the code that was sent to your mobile
          number.{' '}
        </BodyR>
      </div>
      <div className="pb-28">
        <CalloutB>
          Code send to <CsMask>{phoneNumber}</CsMask>{' '}
        </CalloutB>
      </div>
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();

          handleOTPSubmission(otpValue, trustDevice);
        }}
      >
        <div className="pb-28">
          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={() => handleOTPSubmission(otpValue, trustDevice)}
            maxLength={6}
            error={otpInputError}
            className="w-full p-2 border border-gray-300 rounded text-center text-2xl tracking-widest justify-center"
          />
          {errorMessage && (
            <div>
              <div className="bg-red-10 rounded-12 p-20 mt-16">
                <DetailB className="text-red-140">{errorMessage}</DetailB>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4 mt-8">
          <Button
            data-testid="confirm-otp-submit"
            className="w-full pb-10"
            variant="primary-yellow"
            type="submit"
            disabled={!isOtpComplete}
          >
            Submit
          </Button>
        </div>
      </form>

      <div
        className={twMerge(
          'flex flex-col gap-4 pb-16 mt-16',
          showTrustDevice && 'pb-0',
        )}
      >
        <Button
          data-testid="confirm-otp-resend"
          className="w-full pt-10"
          variant={'secondary'}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            handleResendClick();
          }}
        >
          Resend Code
        </Button>
        <div role="status" aria-live="polite">
          {codeResent && (
            <div data-testid="confirm-otp-resent" className="mt-16">
              <Notification variant="infoGreen">
                <div className="flex gap-8 items-center font-bold">
                  <Image src={GreenBulletCheck} alt="" width={20} height={20} />
                  <DetailB className="text-green-160">
                    We’ve sent a new code to your mobile number.
                  </DetailB>
                </div>
              </Notification>
            </div>
          )}
        </div>
      </div>
      {/* A complete code submits straight away, so this has to be ticked
          before the code is entered. The honeypot Checkbox brings its own
          28px bottom margin, which is the gap to the FAQs. */}
      {showTrustDevice && (
        <div data-testid="confirm-otp-trust-device" className="mt-28">
          <Checkbox
            control={control}
            name="trustDevice"
            label="Don't ask me again for 30 days"
            classNames={{ label: 'self-center' }}
          />
        </div>
      )}
      <div
        className={twMerge(
          'mt-28 flex flex-col gap-16',
          showTrustDevice && 'mt-0',
        )}
      >
        <DetailB className="text-grey-1">
          About Two-Factor Authentication
        </DetailB>
        <Accordion items={twoFaFaqItems} />
      </div>
    </>
  );
};

export default ConfirmItsYou;
